import React, {useContext, useEffect, useMemo, useRef} from 'react';
import {ActionType, CustomerServiceDashboardContext, createMonitoringLogEvent} from '../../context';
import styles from './ContractPanel.module.scss';
import classNames from 'classnames/bind';
import {useStore} from '../../../hooks/use-store';
import {ModalHandle, uuidv4generate} from 'Mri.Web.Common';
import {Server} from 'http';
import {ActiveDisplay} from '../../../Displays/ActiveDisplay';
import {Button, ButtonGroup, Table} from 'reactstrap';
import {LinkContainer} from 'react-router-bootstrap';
import {MonitoringLog} from '../../../../Controllers/CustomerServiceDashboard/CustomerServiceDashboard';
import {BsCloudSlashFill, BsFillCheckCircleFill, BsFillCloudSlashFill} from 'react-icons/bs';
import {useCustomerServiceDashboardStore} from "../../store";
import LoadingWrapper from "../../../generic-components/LoadingWrapper";

const classes = classNames.bind(styles);

export interface ContractPanelProps {
    modalHandle: ModalHandle;
}

export const ContractPanel = (props: ContractPanelProps) => {
    const {modalHandle} = props;
    // const { state, dispatch } = useContext(CustomerServiceDashboardContext);
    const {
        createMonitoringLogEvent,
        selectedContract,
        loading,
        displaysByContract,
        selectDisplay,
        fetchDisplaysByContract
    } = useCustomerServiceDashboardStore();
    // const { selectedContract } = state;
    const {displaysStore, accountStore} = useStore();
    const isMounted = useRef(true);
    const displaySearchResults = selectedContract?.serviceCO
        ? displaysByContract[selectedContract.serviceCO]?.displays
        : null;


    const openNewEventLogModal = (display: ActiveDisplay) => {
        selectDisplay(display);
        // dispatch({ type: ActionType.SELECT_DISPLAY, payload: display });
        modalHandle.open();
    };

    // navigate to the display details page found at route /displays/:serialNumber
    const viewDisplay = (display: ActiveDisplay) => {
        const url = `/displays/${display.serialNumber}`;
        window.open(url, '_blank');
        newMonitoringLogEvent(display);
    };

    const newMonitoringLogEvent = async (display: ActiveDisplay) => {
        const monitoringLog = {
            accountNumber: Number(display.account),
            displaySerial: display.serialNumber,
            userName: accountStore.user?.userName ?? '',
            incidentType: 0,
            description: `Display monitored by ${accountStore.user?.userName}`,
            resolution: '',
            resolved: true,
        } as MonitoringLog;

        createMonitoringLogEvent(monitoringLog);
    };

    useEffect(() => {
        if (!selectedContract) return;
        fetchDisplaysByContract(selectedContract.serviceCO);
    }, [selectedContract]);

    const HeaderRow = (
        <tr className={classes('display-row', 'header-row')}>
            <td className={classes('row-number')}>Row</td>
            <td className={classes('serial-number')}>Serial Number</td>
            <td className={classes('display-status')}>Display Status</td>
            <td className={classes('status-message')}>Status Message</td>
            <td className={classes('actions')}>Actions</td>
        </tr>
    );
    const DisplayListRow = ({display, rowNumber = 0}: { display: ActiveDisplay; rowNumber: number }) => {
        return (
            <tr className={classes('display-row')}>
                <td className={classes('row-number')}>{rowNumber}</td>
                <td className={classes('serial-number')}>{display?.serialNumber}</td>
                <td className={classes('display-status')}>{display?.statusMessageBrief}</td>
                <td className={classes('status-message')}>
                    {display.statusMessage && display.statusMessage === 'good' && <BsFillCheckCircleFill/>}
                    {display.statusMessage && display?.statusMessage === 'offline' && <BsCloudSlashFill/>}
                    {display?.statusMessage}
                </td>
                <td>
                    <ButtonGroup className={classes('actions')}>
                        <Button
                            className={classes('btn btn-primary', 'action-button')}
                            onClick={() => viewDisplay(display)}
                        >
                            View Display
                        </Button>
                        <Button
                            className={classes('action-button', 'btn btn-primary')}
                            onClick={() => openNewEventLogModal(display)}
                        >
                            Create Log
                        </Button>
                    </ButtonGroup>
                </td>
            </tr>
        );
    };

    const DisplayList = useMemo(() => {
        if (!displaySearchResults?.length && !loading.displaysByContract) return <div>No Displays</div>;
        if (loading.displaysByContract) return <div>Loading Displays...</div>;
        return (
            <Table hover responsive className={classes('display-list')}>
                <thead>{HeaderRow}</thead>
                <tbody>
                {selectedContract && displaysByContract[selectedContract.serviceCO].displays.map(
                    (display: any, index: number) => {
                        const rowNumber = index + 1;
                        return (
                            <DisplayListRow
                                key={`row-${rowNumber}__display-${display.serialNumber}`}
                                display={display}
                                rowNumber={rowNumber}
                            />
                        );
                    }
                )}
                </tbody>
            </Table>
        );
    }, [displaySearchResults, loading.displaysByContract]);

    return (
        <>
            {selectedContract && (
                <div className={classes('card', 'contract-panel')}>
                    <div className={classes('header')}>
                        <div className={classes('contract')}>Contract: {selectedContract.serviceCO}</div>
                        <div className={classes('service-level')}>{selectedContract.serviceLevel}</div>
                    </div>
                    <LoadingWrapper loading={loading.displaysByContract}>
                        {DisplayList}
                    </LoadingWrapper>
                </div>
            )}
            {!selectedContract && (
                <div className={classes('card', 'contract-panel')}>
                    <div className={classes('header')}>
                        <div className={classes('contract')}>Select a Contract</div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ContractPanel;